import { baseApi, type MoneyDto } from '@shared/api/baseApi';

export interface ProductDto {
  id: string;
  slug: string;
  name: string;
  details: string | null;
  stock: number;
  inStock: boolean;
  inventoryMode: 'PROVIDER' | 'UNLIMITED' | 'UNIQUE_ITEMS';
  deliveryMode: 'PROVIDER' | 'SHARED_MESSAGE' | 'MANUAL_QUEUE' | 'STOCK_ITEM';
  price: MoneyDto;
  /** Crossed-out original; null when no discount applies. */
  listPrice: MoneyDto | null;
  discountLabel: string | null;
  flashDeal: { id: string; endsAt: string } | null;
  /** Admin-selected Popular ribbon and highlighted purchase action. */
  popular: boolean;
  logoUrl: string;
  /** Something the buyer must supply before paying; null on most products. */
  input: { type: 'TEXT' | 'NUMBER' | 'EMAIL'; placeholder: string | null } | null;
  /** Which storefront section it belongs to; null while unfiled. */
  category: CategoryDto | null;
  /**
   * A phone number rented to receive one verification code. Buying one opens a
   * live session instead of ending in a delivery, so the buy sheet and the
   * order card both behave differently for it.
   */
  isNumber: boolean;
}

export interface CategoryDto {
  id: string;
  slug: string;
  name: string;
}

export interface ProductCatalogDto {
  products: ProductDto[];
  serverTime: string;
  featuredFlashProductId: string | null;
  /** Captured when the response arrives, for device-clock correction. */
  clientReceivedAt: number;
}

export const productApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getProducts: builder.query<ProductCatalogDto, void>({
      query: () => '/products',
      transformResponse: (response: Omit<ProductCatalogDto, 'clientReceivedAt'>) => ({
        ...response,
        clientReceivedAt: Date.now(),
      }),
      providesTags: ['Product'],
    }),
    /**
     * The storefront's section tabs.
     *
     * Empty until an operator makes one, which is the shop's original
     * behaviour: with no sections the store renders a single flat list.
     */
    getCategories: builder.query<CategoryDto[], void>({
      query: () => '/categories',
      transformResponse: (response: { categories: CategoryDto[] }) => response.categories,
      providesTags: ['Category'],
    }),
  }),
});

export const { useGetProductsQuery, useGetCategoriesQuery } = productApi;
